import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, take, tap } from 'rxjs/operators';

import { AbiProfileDetailsModel } from '@data-layer/models/companies';
import { SelfFilerAbiClientRepresentativeFormComponent } from '../self-filer-abi-client-representative-form';
import { EntitiesRepository, SelfFilerRepository } from '@data-layer/repositories';
import { SelfFilerService } from '@services/self-filer.service';
import { EntityModel } from '@data-layer/models/entities';
import { enumFilerCodeState } from '@enum/enumFilerCodeState';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';

@Component({
  selector: 'self-filer-abi-profile-form',
  templateUrl: './self-filer-abi-profile-form.component.html',
  styleUrls: ['./self-filer-abi-profile-form.component.scss'],
})
export class SelfFilerAbiProfileFormComponent implements OnInit {
  @Input() public abiProfileDetails: AbiProfileDetailsModel;
  @Input() public companyId: number;
  @Input() public hasFilerCode: boolean = false;
  @Output() public update: EventEmitter<any> = new EventEmitter();

  @ViewChild(SelfFilerAbiClientRepresentativeFormComponent, { static: false })
  public formComponent: SelfFilerAbiClientRepresentativeFormComponent;
  public isOpenForm: boolean = false;
  public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.DEFAULT;
  public menuMode: enumDropdownActionMenuMode = enumDropdownActionMenuMode.LIGHT;

  constructor(
    private router: Router,
    private entitiesRepository: EntitiesRepository,
    private selfFilerService: SelfFilerService,
    private selfFilerRepository: SelfFilerRepository,
    private ga: GoogleAnalyticsService,
  ) {}

  public ngOnInit(): void {}

  public get isSuccess() {
    return this.selfFilerService.getOnBoardingObjectState(this.abiProfileDetails) === enumFilerCodeState.SUCCESS;
  }

  public get currentUrl() {
    return this.router.url;
  }

  public openForm() {
    this.isOpenForm = true;
  }

  public clearAndSave() {
    this.selfFilerRepository
      .deleteAbiClient(this.companyId)
      .pipe(
        tap(() => {
          this.ga.emitCustomEvent(enumGATagEventCategory.OnBoarding, enumGATagEventAction.OBAbiProfileUpdateInfo, `Delete`);
        }),
        take(1),
      )
      .subscribe(() => {
        this.update.emit();
        this.isOpenForm = false;
      });
  }

  public save() {
    const entityToSave = this.formComponent.fg.value;
    this.createOrUpdateEntity(entityToSave)
      .pipe(
        switchMap((entity) => this.selfFilerRepository.setAbiClient(entity.id, this.companyId)),
        tap(() => {
          this.ga.emitCustomEvent(enumGATagEventCategory.OnBoarding, enumGATagEventAction.OBAbiProfileUpdateInfo, `Update`);
        }),
        take(1),
      )
      .subscribe(() => {
        this.update.emit();
        this.isOpenForm = false;
      });
  }

  public createOrUpdateEntity(entity: EntityModel) {
    if (entity.id) {
      return this.entitiesRepository.updateEntity(entity.id, entity);
    } else {
      return this.entitiesRepository.createEntity(this.companyId, entity);
    }
  }
}
